import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * JsonLd — injects Schema.org JSON-LD as a <script type="application/ld+json"> tag.
 */
export function JsonLd({ data }) {
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://nehchaleye.com";

export const orgLogo =
  "https://customer-assets-v7afamib.emergentagent.net/job_cataract-seo-noida/artifacts/juyfcelq_CLINICLOGO.jpg";

export function medicalBusinessSchema(clinic) {
  return {
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "MedicalClinic", "LocalBusiness"],
    "@id": `${SITE_URL}/#clinic`,
    name: clinic.name,
    image: orgLogo,
    logo: orgLogo,
    url: SITE_URL,
    telephone: clinic.phoneRaw,
    email: clinic.email,
    priceRange: "₹₹",
    address: {
      "@type": "PostalAddress",
      streetAddress: clinic.address.line1,
      addressLocality: clinic.address.locality,
      addressRegion: clinic.address.region,
      postalCode: clinic.address.postal,
      addressCountry: clinic.address.countryCode,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: clinic.geo.lat,
      longitude: clinic.geo.lng,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
        opens: "09:00",
        closes: "19:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Sunday",
        opens: "10:00",
        closes: "13:00",
      },
    ],
    medicalSpecialty: ["Ophthalmic", "Optometric"],
    areaServed: [
      { "@type": "City", name: "Noida" },
      { "@type": "City", name: "Greater Noida" },
      { "@type": "City", name: "Ghaziabad" },
      { "@type": "AdministrativeArea", name: "Delhi NCR" },
    ],
    sameAs: [],
  };
}

export function physicianSchema(clinic) {
  return {
    "@context": "https://schema.org",
    "@type": "Physician",
    "@id": `${SITE_URL}/about#physician`,
    name: "Dr. Gurkiran Kaur",
    image: clinic.doctorPhoto,
    medicalSpecialty: "Ophthalmic",
    jobTitle: "Fellowship Trained Ophthalmologist",
    worksFor: { "@id": `${SITE_URL}/#clinic` },
    alumniOf: [
      { "@type": "CollegeOrUniversity", name: "Armed Forces Medical College, Pune" },
      { "@type": "CollegeOrUniversity", name: "KGMU, Lucknow" },
      { "@type": "CollegeOrUniversity", name: "LVPEI, Hyderabad" },
    ],
    knowsAbout: [
      "Cataract Surgery",
      "Oculoplasty",
      "Glaucoma Management",
      "Pediatric Eye Care",
      "Refractive Surgery",
      "Diabetic Retinopathy",
    ],
    url: `${SITE_URL}/about`,
    telephone: clinic.phoneRaw,
  };
}

export function medicalProcedureSchema(service, clinic) {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: service.name,
    description: service.what,
    procedureType: "https://schema.org/SurgicalProcedure",
    bodyLocation: "Eye",
    performer: {
      "@type": "Physician",
      name: "Dr. Gurkiran Kaur",
      medicalSpecialty: "Ophthalmic",
    },
    availableService: {
      "@type": "MedicalClinic",
      name: clinic.name,
      "@id": `${SITE_URL}/#clinic`,
    },
    url: `${SITE_URL}/services/${service.slug}`,
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function reviewsSchema(clinic, testimonials) {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "@id": `${SITE_URL}/#clinic`,
    name: clinic.name,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: clinic.ratingValue || "5.0",
      reviewCount: clinic.reviewCount || testimonials.length,
      bestRating: "5",
    },
    review: testimonials.map((t) => ({
      "@type": "Review",
      author: { "@type": "Person", name: t.name },
      reviewBody: t.text,
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(t.rating),
        bestRating: "5",
      },
    })),
  };
}

export function articleSchema(post) {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    headline: post.title,
    description: post.excerpt,
    author: {
      "@type": "Physician",
      name: post.author || "Dr. Gurkiran Kaur",
      medicalSpecialty: "Ophthalmic",
    },
    datePublished: post.created_at,
    dateModified: post.updated_at,
    url: `${SITE_URL}/blog/${post.slug}`,
    image: post.cover_image || orgLogo,
    publisher: {
      "@type": "Organization",
      name: "Nehchal Eye Centre",
      logo: { "@type": "ImageObject", url: orgLogo },
    },
  };
}
